import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { getAuthCallbackUrl } from "@/lib/auth";
import AuthShell from "@/pages/auth/components/AuthShell";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [code, setCode] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    const { error: err } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (err) {
      if (err.message.toLowerCase().includes("not confirmed")) {
        setNeedsConfirmation(true);
        setError(
          "Votre email n'a pas encore été confirmé. Saisissez le code reçu par email, ou renvoyez-le."
        );
      } else {
        setError("Email ou mot de passe incorrect.");
      }
      setLoading(false);
      return;
    }
    navigate("/admin");
  }

  async function handleResend() {
    setResendLoading(true);
    setInfo("");
    const { error: err } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: getAuthCallbackUrl() },
    });
    setResendLoading(false);
    if (err) {
      setError(err.message);
    } else {
      setInfo("Un nouveau code vient d'être envoyé à votre adresse email.");
    }
  }

  async function handleConfirmCode(e: FormEvent) {
    e.preventDefault();
    setResendLoading(true);
    setError("");
    const { error: err } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "signup",
    });
    setResendLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    navigate("/admin");
  }

  return (
    <AuthShell
      title="Connexion"
      subtitle="Accédez à l'espace de gestion de l'association."
    >
      {!needsConfirmation ? (
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Mot de passe
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputClass}
            />
          </div>

          {error && (
            <p className="text-sm text-primary-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Connexion…
              </>
            ) : (
              "Se connecter"
            )}
          </button>

          <div className="flex items-center justify-between text-sm pt-1">
            <Link
              to="/auth/forgot-password"
              className="text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              Mot de passe oublié ?
            </Link>
            <Link
              to="/auth/register"
              className="text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              Créer un compte
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={handleConfirmCode} className="space-y-4">
          <p className="text-sm text-foreground-600">{error}</p>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Code de confirmation
            </label>
            <input
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={inputClass}
            />
          </div>

          {info && <p className="text-sm text-foreground-600">{info}</p>}

          <button
            type="submit"
            disabled={resendLoading}
            className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
          >
            Confirmer
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading}
            className="w-full text-sm text-primary-600 hover:text-primary-700 cursor-pointer disabled:opacity-60"
          >
            Renvoyer le code
          </button>
        </form>
      )}
    </AuthShell>
  );
}